import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Vote, 
  Plus, 
  Users, 
  Calendar, 
  FileText, 
  CheckCircle2, 
  Clock,
  Edit,
  Trash2,
  Download,
  MapPin,
  ListChecks,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipi di assemblea
const TIPI_ASSEMBLEA = [
  { value: 'ordinaria', label: 'Assemblea Ordinaria' },
  { value: 'straordinaria', label: 'Assemblea Straordinaria' },
  { value: 'elettiva', label: 'Assemblea Elettiva' }
];

// Stati assemblea
const STATI_ASSEMBLEA = [
  { value: 'programmata', label: 'Programmata', color: 'bg-blue-100 text-blue-700' },
  { value: 'convocata', label: 'Convocata', color: 'bg-amber-100 text-amber-700' },
  { value: 'in_corso', label: 'In Corso', color: 'bg-green-100 text-green-700' },
  { value: 'conclusa', label: 'Conclusa', color: 'bg-slate-100 text-slate-700' },
  { value: 'annullata', label: 'Annullata', color: 'bg-red-100 text-red-700' }
];

export default function Assemblee() {
  const { user } = useAuth();
  const [assemblee, setAssemblee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAssemblea, setEditingAssemblea] = useState(null);
  const [selectedAssemblea, setSelectedAssemblea] = useState(null);
  
  const [formData, setFormData] = useState({
    tipo: 'ordinaria',
    data: '',
    ora: '18:00',
    luogo: '',
    ordine_del_giorno: '',
    note: ''
  });

  useEffect(() => {
    fetchAssemblee();
  }, []);

  const fetchAssemblee = async () => {
    try {
      const response = await fetch(`${API_URL}/api/governance/assemblee`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAssemblee(data.assemblee || []);
      }
    } catch (error) {
      console.error('Error fetching assemblee:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingAssemblea 
        ? `${API_URL}/api/governance/assemblee/${editingAssemblea.assemblea_id}`
        : `${API_URL}/api/governance/assemblee`;
      
      const response = await fetch(url, {
        method: editingAssemblea ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingAssemblea ? 'Assemblea aggiornata' : 'Assemblea creata');
        setShowAddDialog(false);
        setEditingAssemblea(null);
        resetForm();
        fetchAssemblee();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleDelete = async (assembleaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa assemblea?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/governance/assemblee/${assembleaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Assemblea eliminata');
        fetchAssemblee();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'ordinaria',
      data: '',
      ora: '18:00',
      luogo: '',
      ordine_del_giorno: '',
      note: ''
    });
  };

  const openEditDialog = (assemblea) => {
    setEditingAssemblea(assemblea);
    setFormData({
      tipo: assemblea.tipo || 'ordinaria',
      data: assemblea.data?.split('T')[0] || '',
      ora: assemblea.ora || '18:00',
      luogo: assemblea.luogo || '',
      ordine_del_giorno: assemblea.ordine_del_giorno || '',
      note: assemblea.note || ''
    });
    setShowAddDialog(true);
  };

  const generateConvocazione = async (assembleaId) => {
    toast.info('Generazione convocazione in corso...');
    try {
      window.open(`${API_URL}/api/governance/assemblee/${assembleaId}/convocazione`, '_blank');
      toast.success('Convocazione generata!');
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  const generateFoglioPresenze = async (assembleaId) => {
    toast.info('Generazione foglio presenze in corso...');
    try {
      window.open(`${API_URL}/api/governance/assemblee/${assembleaId}/presenze`, '_blank');
      toast.success('Foglio presenze generato!');
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  // Filtra assemblee
  const assembleeInProgramma = assemblee.filter(a => 
    ['programmata', 'convocata', 'in_corso'].includes(a.stato)
  );
  const assembleePassate = assemblee.filter(a => 
    ['conclusa', 'annullata'].includes(a.stato)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assemblee-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Vote className="w-7 h-7 text-indigo-600" />
            Assemblee
          </h1>
          <p className="text-slate-600 mt-1">
            Gestisci le assemblee dei soci, convocazioni e verbali
          </p>
        </div>
        <Button onClick={() => { resetForm(); setEditingAssemblea(null); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nuova Assemblea
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{assembleeInProgramma.length}</p>
                <p className="text-sm text-slate-500">In Programma</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {assemblee.filter(a => a.stato === 'conclusa').length}
                </p>
                <p className="text-sm text-slate-500">Concluse</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {assemblee.filter(a => a.verbale_generato).length}
                </p>
                <p className="text-sm text-slate-500">Con Verbale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{assemblee.length}</p>
                <p className="text-sm text-slate-500">Totale Anno</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="programma" className="space-y-4">
        <TabsList>
          <TabsTrigger value="programma">In Programma ({assembleeInProgramma.length})</TabsTrigger>
          <TabsTrigger value="archivio">Archivio ({assembleePassate.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="programma">
          {assembleeInProgramma.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Vote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna assemblea in programma</h3>
                <p className="text-slate-500 mb-4">Programma la prossima assemblea dei soci</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Programma Assemblea
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {assembleeInProgramma.map((assemblea) => {
                const statoInfo = STATI_ASSEMBLEA.find(s => s.value === assemblea.stato) || STATI_ASSEMBLEA[0];
                const tipoInfo = TIPI_ASSEMBLEA.find(t => t.value === assemblea.tipo) || TIPI_ASSEMBLEA[0];
                
                return (
                  <Card key={assemblea.assemblea_id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {tipoInfo.label}
                            <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-4 mt-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {assemblea.data ? new Date(assemblea.data).toLocaleDateString('it-IT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              }) : 'Data da definire'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {assemblea.ora || '18:00'}
                            </span>
                            {assemblea.luogo && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {assemblea.luogo}
                              </span>
                            )}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {assemblea.ordine_del_giorno && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1 mb-2">
                            <ListChecks className="w-4 h-4" />
                            Ordine del Giorno
                          </h4>
                          <p className="text-sm text-slate-600 whitespace-pre-line bg-slate-50 p-3 rounded-lg">
                            {assemblea.ordine_del_giorno}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(assemblea)}>
                          <Edit className="w-3 h-3 mr-1" />
                          Modifica
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => generateConvocazione(assemblea.assemblea_id)}>
                          <Send className="w-3 h-3 mr-1" />
                          Genera Convocazione
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => generateFoglioPresenze(assemblea.assemblea_id)}>
                          <FileText className="w-3 h-3 mr-1" />
                          Foglio Presenze
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(assemblea.assemblea_id)}>
                          <Trash2 className="w-3 h-3 mr-1" />
                          Elimina
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archivio">
          {assembleePassate.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Archivio vuoto</h3>
                <p className="text-slate-500">Le assemblee concluse appariranno qui</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {assembleePassate.map((assemblea) => {
                    const tipoInfo = TIPI_ASSEMBLEA.find(t => t.value === assemblea.tipo) || TIPI_ASSEMBLEA[0];
                    const statoInfo = STATI_ASSEMBLEA.find(s => s.value === assemblea.stato) || STATI_ASSEMBLEA[4];
                    
                    return (
                      <div key={assemblea.assemblea_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{tipoInfo.label}</p>
                          <p className="text-sm text-slate-500">
                            {assemblea.data ? new Date(assemblea.data).toLocaleDateString('it-IT') : 'N/D'}
                            {assemblea.partecipanti_count && ` • ${assemblea.partecipanti_count} partecipanti`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {assemblea.verbale_generato && (
                            <Button variant="outline" size="sm">
                              <Download className="w-3 h-3 mr-1" />
                              Verbale
                            </Button>
                          )}
                          <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAssemblea ? 'Modifica Assemblea' : 'Nuova Assemblea'}</DialogTitle>
            <DialogDescription>
              {editingAssemblea ? 'Modifica i dettagli dell\'assemblea' : 'Programma una nuova assemblea dei soci'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo di Assemblea *</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_ASSEMBLEA.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input 
                  type="date" 
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Ora</Label>
                <Input 
                  type="time" 
                  value={formData.ora}
                  onChange={(e) => setFormData({...formData, ora: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Luogo</Label>
              <Input 
                value={formData.luogo}
                onChange={(e) => setFormData({...formData, luogo: e.target.value})}
                placeholder="Sede dell'associazione, Via..."
              />
            </div>

            <div>
              <Label>Ordine del Giorno</Label>
              <Textarea 
                value={formData.ordine_del_giorno}
                onChange={(e) => setFormData({...formData, ordine_del_giorno: e.target.value})}
                placeholder="1. Approvazione bilancio&#10;2. Rinnovo cariche sociali&#10;3. Varie ed eventuali"
                rows={5}
              />
            </div>

            <div>
              <Label>Note</Label>
              <Input 
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                {editingAssemblea ? 'Salva Modifiche' : 'Crea Assemblea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
